import React, { useState } from 'react';

import {
  IconCornerDownRight,
  IconEdit,
  IconEye,
  IconEyeOff,
  IconTrash,
} from '@tabler/icons-react';
import { useTranslation } from 'react-i18next';

import {
  IconButton,
  Tag,
  TextField,
  TextFieldType,
  Typography,
} from '@cfa/react-components';

import { itemByLanguage } from '@/utils/language';

import PopoverMenuButton from '@/components/PopoverMenuButton/PopoverMenuButton';
import PopoverMenuButtonItem from '@/components/PopoverMenuButton/PopoverMenuButtonItem';

import { IntlSubcategoryDTO } from '@/services/content-api-types';

import { setAdminState, useFocusAdminState } from '@/routes/Admin/state';

interface HeaderProps {
  countryCode: string;
  index: number;
  showAdminFunctionality: boolean;
  subcategoryName: Record<string, string> | undefined;
  userIsAdmin: boolean;
  subcategory: IntlSubcategoryDTO;
  turnOffAllEditing: number;
  setEnableCategoryButtonsFromChildren: (value: boolean) => void;
  translationRequired: boolean;
}

const Header: React.FC<HeaderProps> = ({
  index,
  showAdminFunctionality,
  subcategoryName,
  userIsAdmin,
  subcategory,
  turnOffAllEditing,
  setEnableCategoryButtonsFromChildren,
  translationRequired,
}) => {
  const { t } = useTranslation();

  const { temporaryCategory, setTemporaryCategory } =
    useFocusAdminState('temporaryCategory');

  const [showEditNameFields, setShowEditNameFields] = useState(false);

  const [prevTurnOffAllEditing, setPrevTurnOffAllEditing] =
    useState(turnOffAllEditing);
  if (prevTurnOffAllEditing !== turnOffAllEditing) {
    setPrevTurnOffAllEditing(turnOffAllEditing);
    setShowEditNameFields(false);
  }

  const onNameChange = (e: React.ChangeEvent<TextFieldType>) => {
    const name = e.target.value;
    if (name.length) {
      setTemporaryCategory({
        ...temporaryCategory,
        subcategories: (temporaryCategory.subcategories ?? []).map((sub, i) =>
          i === index
            ? {
                ...sub,
                name: { en: name, es: sub.name?.es ?? '' },
              }
            : sub,
        ),
      });
    } else {
      setTemporaryCategory({
        ...temporaryCategory,
        subcategories: (temporaryCategory.subcategories ?? []).map((sub, i) =>
          i === index
            ? {
                ...sub,
                name: { en: '', es: sub.name?.es ?? '' },
              }
            : sub,
        ),
      });
    }
  };

  const onTranslationChange = (e: React.ChangeEvent<TextFieldType>) => {
    const translation = e.target.value;
    if (translation.length) {
      setTemporaryCategory({
        ...temporaryCategory,
        subcategories: (temporaryCategory.subcategories ?? []).map((sub, i) =>
          i === index
            ? {
                ...sub,
                name: { en: sub.name?.en ?? '', es: translation },
              }
            : sub,
        ),
      });
    } else {
      setTemporaryCategory({
        ...temporaryCategory,
        subcategories: (temporaryCategory.subcategories ?? []).map((sub, i) =>
          i === index
            ? {
                ...sub,
                name: { en: sub.name?.en ?? '', es: '' },
              }
            : sub,
        ),
      });
    }
  };

  const toggleVisibilityModal = () => {
    setAdminState('selectedSubcategory', subcategory);
    setAdminState('confirmationModalType', 'subcategoryVisibility');
  };

  return (
    <div className="flex w-full items-center">
      {showEditNameFields ? (
        <>
          <div
            className="flex w-full flex-col items-center gap-2 px-4 py-0"
            onClickCapture={e => e.stopPropagation()}
            onPointerDownCapture={e => e.stopPropagation()}
          >
            <div className="flex w-full flex-col text-base/6 font-normal">
              <TextField
                data-testid={'EditSubcategoryNameInput'}
                fullWidth
                label={t('Admin.subcategory')}
                onChange={onNameChange}
                placeholder={'Enter Name'}
                required
                value={temporaryCategory.subcategories?.[index]?.name?.en ?? ''}
              />
            </div>
            <div className="flex w-full">
              <IconCornerDownRight size={35} />
              <TextField
                data-testid={'EditSubcategoryTranslationInput'}
                fullWidth
                label={t('Admin.spanishTranslation')}
                onChange={onTranslationChange}
                placeholder={'Enter Translation'}
                required={translationRequired}
                value={temporaryCategory.subcategories?.[index]?.name?.es}
              />
            </div>
          </div>
          {userIsAdmin && showAdminFunctionality && (
            <div className="flex items-center gap-3">
              {subcategory.enabled ? (
                <Tag
                  className="ml-4! flex! h-fit! flex-row-reverse! items-center! [&_.tag-leading-element]:my-0! [&_.tag-leading-element]:mr-0! [&_.tag-leading-element]:ml-1.5! [&_.tag-leading-element]:size-auto!"
                  label={t('Admin.categoryIsVisible')}
                  leadingElement={<IconEye width={10} />}
                  onClick={e => {
                    e.stopPropagation();
                    toggleVisibilityModal();
                  }}
                  variant="filled"
                />
              ) : (
                <button
                  type="button"
                  className="ml-4 cursor-pointer"
                  onClick={e => {
                    e.stopPropagation();
                    toggleVisibilityModal();
                  }}
                >
                  <Tag
                    className="flex! h-fit! flex-row-reverse! items-center! [&_.tag-leading-element]:my-0! [&_.tag-leading-element]:mr-0! [&_.tag-leading-element]:ml-1.5! [&_.tag-leading-element]:size-auto!"
                    disabled
                    label={t('Admin.categoryHidden')}
                    leadingElement={<IconEyeOff width={10} />}
                    variant="filled"
                  />
                </button>
              )}
              <IconButton
                aria-label="Delete Category"
                color="default"
                data-testid="EditCategoryTrashIcon"
                href=""
                onClick={() => {
                  setAdminState('selectedSubcategory', subcategory);
                  setAdminState('selectedSubcategoryIndex', index);
                  setAdminState('confirmationModalType', 'subcategoryDelete');
                }}
                size="md"
                className="ml-auto!"
              >
                <IconTrash />
              </IconButton>
            </div>
          )}
        </>
      ) : (
        <div className="flex w-full flex-row items-center justify-between">
          <Typography variant="body1" className="ml-4!">
            {itemByLanguage(subcategoryName, '')}
          </Typography>
          {userIsAdmin && showAdminFunctionality && (
            <div className="flex items-center gap-3">
              {subcategory.enabled ? (
                <Tag
                  className="ml-4! flex! h-fit! flex-row-reverse! items-center! [&_.tag-leading-element]:my-0! [&_.tag-leading-element]:mr-0! [&_.tag-leading-element]:ml-1.5! [&_.tag-leading-element]:size-auto!"
                  data-testid="EditSubcategoryToggleShow"
                  label={t('Admin.categoryIsVisible')}
                  leadingElement={<IconEye width={10} />}
                  onClick={e => {
                    e.stopPropagation();
                    toggleVisibilityModal();
                  }}
                  variant="filled"
                />
              ) : (
                <button
                  type="button"
                  className="ml-4 cursor-pointer"
                  onClick={e => {
                    e.stopPropagation();
                    toggleVisibilityModal();
                  }}
                >
                  <Tag
                    className="flex! h-fit! flex-row-reverse! items-center! [&_.tag-leading-element]:my-0! [&_.tag-leading-element]:mr-0! [&_.tag-leading-element]:ml-1.5! [&_.tag-leading-element]:size-auto!"
                    data-testid="EditSubcategoryToggleHide"
                    disabled
                    label={t('Admin.categoryHidden')}
                    leadingElement={<IconEyeOff width={10} />}
                    variant="filled"
                  />
                </button>
              )}
              <PopoverMenuButton>
                <PopoverMenuButtonItem
                  icon={<IconEdit />}
                  onClick={() => {
                    setShowEditNameFields(true);
                    setEnableCategoryButtonsFromChildren(true);
                  }}
                  text={t('Generic.rename')}
                />
                <PopoverMenuButtonItem
                  icon={<IconTrash />}
                  isDestructive={true}
                  onClick={() => {
                    setAdminState('selectedSubcategoryIndex', index);
                    setAdminState('selectedSubcategory', subcategory);
                    setAdminState('confirmationModalType', 'subcategoryDelete');
                  }}
                  text={t('Button.delete')}
                />
              </PopoverMenuButton>
            </div>
          )}
        </div>
      )}
    </div>
  );
};

export default Header;
